import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Paper,
  Collapse,
  IconButton,
  TextField,
  MenuItem,
  InputBase,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, LocationOn } from '@mui/icons-material';
import { Loader } from '../common/Loader';
import { useAppointments } from '../../hooks/useAppointments';
import { Appointment, appointmentService, PreparationGuide, CancellationReason } from '../../services/appointmentService';

interface AppointmentsListProps {
  type?: 'upcoming' | 'past' | 'all';
}

const getStatusColor = (status: string): any => {
  switch (status) {
    case 'scheduled':
      return 'primary';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'scheduled':
      return 'Запланирован';
    case 'completed':
      return 'Завершён';
    case 'cancelled':
      return 'Отменён';
    default:
      return status;
  }
};

interface DialogState {
  type: 'cancel' | 'reschedule' | 'guides' | null;
  appointmentId: string | null;
}

export const AppointmentsList: React.FC<AppointmentsListProps> = ({ type = 'upcoming' }) => {
  const navigate = useNavigate();
  const { appointments, upcomingAppointments, pastAppointments, isLoading, isMatched, error, cancelAppointment } =
    useAppointments();
  const [expandedMemos, setExpandedMemos] = useState<Set<string>>(new Set());

  // Dialog states
  const [dialog, setDialog] = useState<DialogState>({ type: null, appointmentId: null });
  const [isProcessing, setIsProcessing] = useState(false);

  // Cancel appointment state
  const [cancellationReasons, setCancellationReasons] = useState<CancellationReason[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [cancelComment, setCancelComment] = useState<string>('');

  // Reschedule state
  const [rescheduleComment, setRescheduleComment] = useState<string>('');

  // Preparation guides state
  const [preparationGuides, setPreparationGuides] = useState<PreparationGuide[]>([]);
  const [guidesLoading, setGuidesLoading] = useState(false);

  const loadCancellationReasons = async () => {
    setReasonsLoading(true);
    try {
      const response = await appointmentService.getCancellationReasons();
      setCancellationReasons(response.reasons || []);
    } catch (err) {
      console.error('Ошибка при загрузке причин отмены:', err);
      setCancellationReasons([]);
    } finally {
      setReasonsLoading(false);
    }
  };

  const handleViewGuidesClick = async (appointmentId: string) => {
    setDialog({ type: 'guides', appointmentId });
    setGuidesLoading(true);
    try {
      const response = await appointmentService.getPreparationGuides(appointmentId);
      setPreparationGuides(response.guides || []);
    } catch (err) {
      console.error('Ошибка при загрузке памятки:', err);
      setPreparationGuides([]);
    } finally {
      setGuidesLoading(false);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!dialog.appointmentId || !rescheduleComment.trim()) return;

    setIsProcessing(true);
    try {
      await appointmentService.createRescheduleRequest(dialog.appointmentId, rescheduleComment);
      setDialog({ type: null, appointmentId: null });
      setRescheduleComment('');
    } catch (err) {
      console.error('Ошибка при переносе записи:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const displayAppointments =
    type === 'upcoming'
      ? upcomingAppointments
      : type === 'past'
      ? pastAppointments
      : appointments;

  useEffect(() => {
    if (dialog.type === 'cancel' && cancellationReasons.length === 0 && !reasonsLoading) {
      loadCancellationReasons();
    }
  }, [dialog.type]);

  const handleCancelClick = (appointmentId: string) => {
    setDialog({ type: 'cancel', appointmentId });
    setSelectedReasonId('');
    setCancelComment('');
  };

  const handleRescheduleClick = (appointmentId: string) => {
    setDialog({ type: 'reschedule', appointmentId });
    setRescheduleComment('');
  };

  const handleConfirmCancel = async () => {
    if (!dialog.appointmentId) return;

    setIsProcessing(true);
    try {
      let comment = '';

      if (selectedReasonId) {
        const selectedReason = cancellationReasons.find(r => r.id === selectedReasonId);
        comment = selectedReason?.name ? `${selectedReason.name}${cancelComment ? ': ' + cancelComment : ''}` : cancelComment;
      } else {
        comment = cancelComment;
      }

      await cancelAppointment(dialog.appointmentId, comment, selectedReasonId ? selectedReasonId : undefined);
      setDialog({ type: null, appointmentId: null });
      setSelectedReasonId('');
      setCancelComment('');
    } catch (err) {
      console.error('Ошибка отмены записи:', err);
    } finally {
      setIsProcessing(false);
    }
  };


  const toggleMemo = (appointmentId: string) => {
    const newExpanded = new Set(expandedMemos);
    if (newExpanded.has(appointmentId)) {
      newExpanded.delete(appointmentId);
    } else {
      newExpanded.add(appointmentId);
    }
    setExpandedMemos(newExpanded);
  };

  const renderMemo = (appointment: Appointment) => {
    if (!appointment.memo && (!appointment.memoImages || appointment.memoImages.length === 0)) {
      return null;
    }

    const isExpanded = expandedMemos.has(appointment.id);

    return (
      <Grid item xs={12}>
        <Box sx={{ marginTop: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleMemo(appointment.id)}>
            <Typography variant="subtitle2" color="primary" sx={{ marginRight: 1 }}>
              Памятка к приёму
            </Typography>
            <IconButton size="small">
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={isExpanded}>
            <Paper sx={{ padding: 2, marginTop: 1, backgroundColor: '#f5f5f5' }}>
              {appointment.memo && (
                <Typography variant="body2" sx={{ marginBottom: 2 }}>
                  {appointment.memo}
                </Typography>
              )}
              {appointment.memoImages && appointment.memoImages.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {appointment.memoImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Памятка ${idx + 1}`}
                      style={{ width: '100%', borderRadius: 8 }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>
      </Grid>
    );
  };

  if (!isMatched) {
    return (
      <Paper sx={{ padding: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          Для просмотра записей необходимо сопоставить карту пациента в разделе «Мой кабинет»
        </Typography>
      </Paper>
    );
  }

  if (isLoading) {
    return <Loader message="Загрузка записей..." />;
  }

  if (error) {
    return (
      <Paper sx={{ padding: 2, backgroundColor: '#ffebee' }}>
        <Typography color="error">Ошибка: {error}</Typography>
      </Paper>
    );
  }

  if (displayAppointments.length === 0) {
    const isUpcoming = type === 'upcoming';
    const iconSrc = isUpcoming ? '/icons/calendar-add-on.svg' : '/icons/calendar-clock.svg';
    const titleLine1 = isUpcoming ? 'На данный момент' : 'Данные о прошлых';
    const titleLine2 = isUpcoming ? 'записи отсутствуют' : 'записях отсутствуют';
    const subtitleLine1 = 'Здесь будут появляться ваши';
    const subtitleLine2 = isUpcoming ? 'активные записи' : 'прошедшие приёмы';

    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 8,
      }}>
        {/* Icon Calendar */}
        <Box sx={{
          margin: '0 auto 16px',
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Box
            component="img"
            src={iconSrc}
            alt="calendar"
            sx={{ width: '40px', height: '40px' }}
          />
        </Box>

        {/* Title Line 1 */}
        <Typography sx={{
          fontSize: '24px',
          fontWeight: 600,
          color: '#212121',
          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          lineHeight: '100%',
          marginBottom: 0,
        }}>
          {titleLine1}
        </Typography>

        {/* Title Line 2 */}
        <Typography sx={{
          fontSize: '24px',
          fontWeight: 600,
          color: '#212121',
          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          lineHeight: '100%',
          marginBottom: '12px',
        }}>
          {titleLine2}
        </Typography>

        {/* Subtitle Line 1 */}
        <Typography sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: '#212121',
          opacity: 0.7,
          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          lineHeight: '120%',
          marginBottom: 0,
        }}>
          {subtitleLine1}
        </Typography>

        {/* Subtitle Line 2 */}
        <Typography sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: '#212121',
          opacity: 0.7,
          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          lineHeight: '120%',
          marginBottom: '72px',
        }}>
          {subtitleLine2}
        </Typography>

        {/* Book Appointment Button */}
        <Button
          sx={{
            width: '100%',
            maxWidth: '360px',
            height: '58px',
            backgroundColor: '#007AFF',
            color: '#FFFFFF',
            textTransform: 'none',
            borderRadius: '24px',
            fontSize: '15px',
            fontWeight: 600,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            '&:hover': {
              backgroundColor: '#0055CC',
            }
          }}
          onClick={() => navigate('/booking')}
        >
          Записаться на приём
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <List>
        {displayAppointments.map((appointment) => (
          <ListItem key={appointment.id} component={Paper} sx={{ marginBottom: 2, padding: 2.5, borderRadius: '24px' }}>
            <Box sx={{ width: '100%' }}>
              <Grid container spacing={1.5}>
                {/* Service Name Header */}
                <Grid item xs={12}>
                  <Typography sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#212121',
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    lineHeight: 1,
                  }}>
                    {appointment.serviceName}
                  </Typography>
                </Grid>

                {/* Date and Time */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="img"
                      src="/icons/calendar-check.svg"
                      sx={{ width: '20px', height: '20px' }}
                    />
                    <Typography sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#212121',
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    }}>
                      {new Date(appointment.date).toLocaleDateString('ru-RU')} {appointment.time}
                    </Typography>
                  </Box>
                </Grid>

                {/* Doctor Name and Specialization */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="img"
                      src="/icons/doctor-specialist.svg"
                      sx={{ width: '20px', height: '20px' }}
                    />
                    <Typography sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#212121',
                      fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      lineHeight: 1,
                    }}>
                      {appointment.doctorName}
                      {appointment.doctorSpecialization && ` - ${appointment.doctorSpecialization}`}
                    </Typography>
                  </Box>
                </Grid>

                {/* Branch Name */}
                {appointment.branchName && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ width: '20px', height: '20px', color: '#007AFF' }} />
                      <Typography sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#212121',
                        fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      }}>
                        {appointment.branchName}
                        {appointment.branchAddress && ` - ${appointment.branchAddress}`}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Memo/Preparation Guide */}
                {renderMemo(appointment)}

                {appointment.status === 'scheduled' && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, marginTop: 2, flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          sx={{
                            width: '153px',
                            height: '58px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #212121',
                            color: '#212121',
                            textTransform: 'none',
                            borderRadius: '24px',
                            fontSize: '15px',
                            fontWeight: 600,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            '&:hover': {
                              backgroundColor: '#F5F5F5',
                            }
                          }}
                          onClick={() => handleRescheduleClick(appointment.id)}
                        >
                          Перенести
                        </Button>
                        <Button
                          sx={{
                            width: '175px',
                            height: '58px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #212121',
                            color: '#212121',
                            textTransform: 'none',
                            borderRadius: '24px',
                            fontSize: '15px',
                            fontWeight: 600,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              backgroundColor: '#F5F5F5',
                            }
                          }}
                          onClick={() => handleCancelClick(appointment.id)}
                        >
                          Отменить приём
                        </Button>
                      </Box>
                      <Button
                        sx={{
                          width: '310px',
                          height: '58px',
                          backgroundColor: '#007AFF',
                          color: '#FFFFFF',
                          textTransform: 'none',
                          borderRadius: '28px',
                          fontSize: '15px',
                          fontWeight: 600,
                          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                          '&:hover': {
                            backgroundColor: '#0055CC',
                          }
                        }}
                        onClick={() => handleViewGuidesClick(appointment.id)}
                      >
                        Памятка перед приёмом
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Cancel Dialog */}
      <Dialog
          open={dialog.type === 'cancel'}
          onClose={() => setDialog({ type: null, appointmentId: null })}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus
        >
        <DialogTitle>Отмена приёма</DialogTitle>
        <DialogContent>
          {reasonsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, marginTop: 2 }}>
              <Loader message="Загрузка причин..." />
            </Box>
          ) : cancellationReasons.length > 0 ? (
            // Если есть причины отмены - показываем выбор причины
            <>
              <Typography variant="body2" sx={{ marginBottom: 2, marginTop: 2 }}>
                Выберите причину отмены:
              </Typography>
              <TextField
                select
                fullWidth
                value={selectedReasonId}
                onChange={(e) => {
                  setSelectedReasonId(e.target.value);
                  setCancelComment('');
                }}
                sx={{ marginBottom: 2 }}
                variant="outlined"
                required
              >
                {cancellationReasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Показываем текстовое поле если выбрана причина, требующая комментария */}
              {(selectedReasonId && cancellationReasons.find(r => r.id === selectedReasonId)?.requiresComment) && (
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1, mt: 2 }}>
                  <InputBase
                    autoFocus
                    multiline
                    fullWidth
                    minRows={4}
                    placeholder="Напишите дополнительное пояснение..."
                    value={cancelComment}
                    onChange={(e) => setCancelComment(e.target.value)}
                  />
                </Box>
              )}
            </>
          ) : (
            // Если причин нет - показываем прямую форму для ввода причины (как в боте)
            <>
              <Typography variant="body2" sx={{ marginBottom: 2, marginTop: 2 }}>
                Напишите причину отмены:
              </Typography>
              <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                <InputBase
                  autoFocus
                  multiline
                  fullWidth
                  minRows={4}
                  placeholder="Укажите причину отмены приёма..."
                  value={cancelComment}
                  onChange={(e) => setCancelComment(e.target.value)}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ type: null, appointmentId: null })}>Нет, оставить</Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={
              isProcessing ||
              reasonsLoading ||
              // Если причин нет - просто требуется заполненный комментарий
              (cancellationReasons.length === 0 && !cancelComment.trim()) ||
              // Если причины есть - ОБЯЗАТЕЛЬНО нужно выбрать причину
              (cancellationReasons.length > 0 && selectedReasonId === '') ||
              // Если выбрана причина, требующая комментария - требуется заполненный комментарий
              (cancellationReasons.length > 0 && selectedReasonId !== '' && cancellationReasons.find(r => r.id === selectedReasonId)?.requiresComment && !cancelComment.trim())
            }
          >
            {isProcessing ? 'Отмена...' : 'Да, отменить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={dialog.type === 'reschedule'} onClose={() => setDialog({ type: null, appointmentId: null })} maxWidth="sm" fullWidth disableEnforceFocus>
        <DialogTitle>Перенести приём</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginBottom: 2, marginTop: 2 }}>
            На какое время вы хотите перенести запись? Опишите ваши предпочтения, и менеджер свяжется с вами для подтверждения.
          </Typography>
          <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1, mt: 2 }}>
            <InputBase
              autoFocus
              multiline
              fullWidth
              minRows={4}
              placeholder="Например: Понедельник 15:00 или вторая половина дня..."
              value={rescheduleComment}
              onChange={(e) => setRescheduleComment(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ type: null, appointmentId: null })}>Отмена</Button>
          <Button onClick={handleConfirmReschedule} variant="contained" disabled={isProcessing || !rescheduleComment.trim()}>
            {isProcessing ? 'Отправка...' : 'Отправить запрос'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preparation Guides Dialog */}
      <Dialog open={dialog.type === 'guides'} onClose={() => setDialog({ type: null, appointmentId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Памятка перед приёмом</DialogTitle>
        <DialogContent>
          {guidesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Loader message="Загрузка памятки..." />
            </Box>
          ) : preparationGuides.length > 0 ? (
            <Box sx={{ marginTop: 2 }}>
              {preparationGuides.map((guide, idx) => (
                <Box key={guide.id || idx} sx={{ marginBottom: 3, pb: 2, borderBottom: idx < preparationGuides.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 1 }}>
                    {guide.name}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#666' }}>
                    {guide.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ marginTop: 2, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Памятки перед приёмом отсутствуют
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ type: null, appointmentId: null })}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};